/**
 * Small Canvas 2D helpers for the figures of the "mathematical process" section.
 */

/**
 * @function prepare Sizes a canvas to its CSS width and the given CSS height, at the device pixel ratio, and clears it.
 * @return {{ctx: CanvasRenderingContext2D, width: Number, height: Number}} Drawing context working in CSS pixels.
 */
export function prepare(canvas, height) {
  const dpr = window.devicePixelRatio || 1;
  const width = canvas.clientWidth;
  canvas.style.height = height + "px";
  if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
  }
  const ctx = canvas.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  return { ctx, width, height };
}

/**
 * @function fitter Maps math coordinates (y up) into a box of the canvas, keeping the aspect ratio.
 * @param {Array<Number>} xs Abscissas to fit.
 * @param {Array<Number>} ys Ordinates to fit.
 * @param {{left: Number, top: Number, width: Number, height: Number}} box Box of the canvas, in CSS pixels.
 */
export function fitter(xs, ys, box) {
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const scale = Math.min(box.width / (maxX - minX || 1), box.height / (maxY - minY || 1));
  const cx = box.left + box.width / 2;
  const cy = box.top + box.height / 2;
  return (x, y) => ({
    x: cx + (x - (minX + maxX) / 2) * scale,
    y: cy - (y - (minY + maxY) / 2) * scale,
  });
}

export function polyline(ctx, points, color, width = 2, close = false) {
  if (points.length === 0) {
    return;
  }
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  if (close) {
    ctx.closePath();
  }
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
}

export function segment(ctx, x0, y0, x1, y1, color, width = 1) {
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
}

/**
 * @function dot A filled dot, with an optional ring of the surface color so it stays readable over lines.
 */
export function dot(ctx, x, y, radius, color, ring = null) {
  if (ring !== null) {
    ctx.beginPath();
    ctx.arc(x, y, radius + 2, 0, 2 * Math.PI);
    ctx.fillStyle = ring;
    ctx.fill();
  }
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, 2 * Math.PI);
  ctx.fillStyle = color;
  ctx.fill();
}

export function circle(ctx, x, y, radius, color, width = 1) {
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, 2 * Math.PI);
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
}

export function label(ctx, text, x, y, color, align = "left", baseline = "middle") {
  ctx.font = "12px " + getComputedStyle(document.body).fontFamily;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = baseline;
  ctx.fillText(text, x, y);
}

/**
 * @function bar A vertical bar growing from a baseline, with a rounded data end (up when height > 0, down otherwise).
 */
export function bar(ctx, x, baseline, width, height, color) {
  const radius = Math.min(4, width / 2, Math.abs(height));
  const top = height > 0 ? baseline - height : baseline;
  const bottom = height > 0 ? baseline : baseline - height;
  ctx.beginPath();
  if (height > 0) {
    ctx.moveTo(x, bottom);
    ctx.lineTo(x, top + radius);
    ctx.arcTo(x, top, x + radius, top, radius);
    ctx.arcTo(x + width, top, x + width, top + radius, radius);
    ctx.lineTo(x + width, bottom);
  } else {
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom - radius);
    ctx.arcTo(x, bottom, x + radius, bottom, radius);
    ctx.arcTo(x + width, bottom, x + width, bottom - radius, radius);
    ctx.lineTo(x + width, top);
  }
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}
